import React, { useCallback, useEffect, useRef, useState } from 'react';

import useAuth from '@hooks/useAuth';
import axiosInstance from '@services/axiosInstance';
import PropTypes from 'prop-types';

import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import _reverse from 'lodash/reverse';
import _orderBy from 'lodash/orderBy';

import moment from 'moment';

BenefitProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

const BENEFITS_URL = `${process.env.REACT_APP_PUBLIC_SITE_URL}/wp-json/tricard_api/v1/benefits`;
const BENEFITS_DESTACADOS_URL = `${process.env.REACT_APP_PUBLIC_SITE_URL}/wp-json/tricard_api/v1/benefits_destacados`;

// La API de WordPress es publica: no se le mandan las credenciales del portal.
const requestPublic = (url) =>
  axiosInstance.request({
    url,
    transformRequest: (_data, headers) => {
      delete headers.common['customer-key'];
      delete headers.common.Authorization;
    },
  });

const toCategories = (data) =>
  _uniqBy(
    _map(data, (d) => ({
      key: _get(d, 'benefit_id', 0),
      label: _get(d, 'benefit_name', ''),
    })),
    'key'
  );

const sortByModified = (data) =>
  _reverse(_sortBy(data, (b) => moment(_get(b, 'fecha_modificada')).toDate()));

export const BenefitContext = React.createContext();

export default function BenefitProvider({ children }) {
  const [{ isAuthenticated }] = useAuth();

  // Destacados: se cargan al entrar al sitio privado (banner y "Beneficios del Club").
  const [loadingDestacados, setLoadingDestacados] = useState(true);
  const [destacados, setDestacados] = useState([]);

  // Listado completo: solo cuando se entra a la pantalla de beneficios.
  const [loading, setLoading] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [categories, setCategories] = useState([]);
  const benefitsRequested = useRef(false);

  useEffect(() => {
    const getDestacados = async () => {
      try {
        const { data } = await requestPublic(BENEFITS_DESTACADOS_URL);
        setDestacados(
          _orderBy(
            data,
            [(b) => new Date(_get(b, 'fecha_modificada', 0))],
            ['desc']
          )
        );
      } catch (error) {
        setDestacados([]);
      } finally {
        setLoadingDestacados(false);
      }
    };

    if (isAuthenticated) {
      getDestacados();
    }
  }, [isAuthenticated]);

  const loadBenefits = useCallback(async () => {
    if (benefitsRequested.current) {
      return;
    }
    benefitsRequested.current = true;
    try {
      const { data } = await requestPublic(BENEFITS_URL);
      setCategories(toCategories(data));
      setBenefits(sortByModified(data));
    } catch (error) {
      // Se permite reintentar la proxima vez que se entre a la pantalla.
      benefitsRequested.current = false;
      setCategories([]);
      setBenefits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Al cerrar sesion se descarta lo cargado, para que el proximo usuario lo pida de nuevo.
  useEffect(() => {
    if (!isAuthenticated) {
      benefitsRequested.current = false;
      setLoading(true);
      setBenefits([]);
      setCategories([]);
      setLoadingDestacados(true);
      setDestacados([]);
    }
  }, [isAuthenticated]);

  return (
    <BenefitContext.Provider
      value={{
        loading,
        benefits,
        categories,
        loadBenefits,
        loadingDestacados,
        destacados,
      }}
    >
      {children}
    </BenefitContext.Provider>
  );
}
